import { Component, EventEmitter, Output, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {TranslateModule} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {NgClass} from '@angular/common';

//Components

//Services
import {BrowserService} from "@services/components/browser.service";
import {AuthService} from "@services/auth/auth.service";
import {RequestMethodsService} from "@services/request/request-methods.service";
import {CityService} from "@services/city/city.service";

//Constants
import {FavoriteAddressInterface} from "@interfaces/orders/favorite-address.interface";
import {EndpointConstant} from "@interfaces/global/endpoint.constant";
import {CityInterface} from "@interfaces/components/cities.interface";
import {MapComponent} from "../../address-selection/map/map.component";
import {SearchLocationComponent} from "../../address-selection/search-location/search-location.component";
import {FullAddressLongLatInterface} from "@interfaces/orders/address.interface";
import {CheckToken} from "@interfaces/auth/login.interface";
import {CloseButtonComponent} from "../../../shared-modules/global/close-button/close-button.component";

@Component({
  selector: 'app-delivery-address',
  templateUrl: './delivery-address.component.html',
  styleUrls: ['./delivery-address.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    TranslateModule,
    MapComponent,
    SearchLocationComponent,
    CloseButtonComponent
  ]
})
export class DeliveryAddressComponent {
  @ViewChild('mapComponent') mapComponent!: MapComponent;
  @ViewChild('searchLocationComponent') searchLocationComponent!: SearchLocationComponent;

  @Output() selectFavoriteAddressEvent = new EventEmitter<void>();
  @Output() closeBottomSheet = new EventEmitter<any>();
  favoriteAddress!: Array<FavoriteAddressInterface>;
  disableSave = true;
  isMobile!: boolean;
  ymaps: any;
  selectedAddressOrCity!: FavoriteAddressInterface | CityInterface | FullAddressLongLatInterface;
  locations: Array<any> = [];
  delivery!:{deliveryPrice: 0} | null;
  isUser: CheckToken = CheckToken.INVALID;
  citySearch = false;
  fromSearch!: boolean;
  addressNameOrCityNameToDisplay = '';

  constructor(
    private browserService: BrowserService,
    private authService: AuthService,
    private requestService: RequestMethodsService,
    private cityService: CityService,
    private dialogRef: MatDialogRef<DeliveryAddressComponent>
  ) {
    this.isMobile = this.browserService.isMobileDevice;
    this.ymaps = (window as any)['ymaps'];
    this.isUser = this.authService.checkToken();
  }

  mapReadyHandler() {
    const address = this.cityService.getCityModelFromLocalStorage();
    if (address) {
      console.log(address)
      this.handleNewLocation(address, true);
      this.checkAndGetUserFavoritesAddresses();
    }
  }

  checkAndGetUserFavoritesAddresses(): void {
    if (this.authService.getUserInfo()) {
      this.getFavoriteAddress();
    }
    if (this.selectedAddressOrCity) {
      this.disableSave = false;
    }
  }

  handleNewLocation(data:{ address: FullAddressLongLatInterface | null } | { city: CityInterface | null}, init = false) {
    if (!data) {
      return;
    }

    if ('address' in data && data.address) {
      this.selectedAddressOrCity = data.address;
      this.mapComponent.map.setCenter([data.address?.latitude, data.address?.longitude])
      this.mapComponent.map.setZoom(18);
    } else if ('city' in data && data.city) {
      if (init) {
        this.citySearch = true;
      }
      this.selectedAddressOrCity = data.city;
      this.mapComponent.map.setCenter([data.city?.latitude, data.city?.longitude])
      this.mapComponent.map.setZoom(8);
      this.mapComponent.mapDiv.nativeElement.click()
    }
    this.displayAddressNameDetection();
  }

  displayAddressNameDetection(): void {
    console.log(this.selectedAddressOrCity)
    if (this.citySearch) {
      if ('cityName' in this.selectedAddressOrCity) {
        this.addressNameOrCityNameToDisplay = this.selectedAddressOrCity.cityName
      }
    } else {
      if (this.selectedAddressOrCity && 'fullAddress' in this.selectedAddressOrCity) {
        this.addressNameOrCityNameToDisplay = this.selectedAddressOrCity.fullAddress;
      }
    }
  }

  getFavoriteAddress(): void {
    this.requestService.get(`${EndpointConstant.favorite}-${EndpointConstant.addresses}`)
      .subscribe((response: Array<FavoriteAddressInterface>) => {
        this.favoriteAddress = response;
      })
  }

  switchSearchType(): void {
    this.searchLocationComponent.inputSearch.nativeElement.value = '';
    this.handleNewLocation(this.cityService.getCityModelFromLocalStorage())
  }

  searchLocation(searchedAddress: string): void {
    this.fromSearch = true;
    this.ymaps.geocode(searchedAddress).then((res: any) => {
      this.locations = res.geoObjects.toArray().filter((geoObject: any) => {
        const address = geoObject.properties.get('metaDataProperty').GeocoderMetaData.Address;
        const addressDetails = geoObject.properties.get('metaDataProperty').GeocoderMetaData.AddressDetails;
        const countryCode = addressDetails.Country?.CountryNameCode;
        if (countryCode !== 'RU') return false;

        const street = address.Components.find((c: any) => c.kind === 'street')?.name || '';
        const house = address.Components.find((c: any) => c.kind === 'house')?.name || '';
        geoObject.streetAndHouse = `${street} ${house}`;

        const province = address.Components.find((c: any) => c.kind === 'province')?.name || '';
        const locality = address.Components.find((c: any) => c.kind === 'locality')?.name || '';
        geoObject.cityAndRegion = `${province}, г. ${locality}`;

        return true;
      });
    });
  }

  close(addressModel: FavoriteAddressInterface | null | boolean = null): void {
    this.dialogRef.close(addressModel)
  }

  confirm(): void {
    if (!this.selectedAddressOrCity) {
      return;
    }

    if (this.isUser === CheckToken.VALID && !this.citySearch) {
      this.saveAddressData();
    } else {
      this.dialogRef.close(this.citySearch? {city: this.selectedAddressOrCity} : {address: this.selectedAddressOrCity})
    }
  }

  saveAddressData(): void {
    let endpoint = `${EndpointConstant.favorite}-${EndpointConstant.addresses}`;
    this.requestService.post(endpoint, this.selectedAddressOrCity).subscribe((response:FavoriteAddressInterface) => {
      this.dialogRef.close({address: response})
    })
  }

  selectAddressInMap(address: FullAddressLongLatInterface | null): void {
    if (this.fromSearch) {
      this.fromSearch = false;
      return;
    }

    if (this.citySearch) {
      this.searchLocationComponent.inputSearch.nativeElement.value = '';
      return;
    }

    if (address) {
      this.selectedAddressOrCity = address;
    }

    if (this.searchLocationComponent.inputSearch) {
      this.searchLocationComponent.inputSearch.nativeElement.value = address?.fullAddress || ''
    }


  }

}
